import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input, Select, Textarea } from '../ui/Input';
import { ReceiptPicker } from './ReceiptPicker';
import { tieneAsociacion } from '../../types/expense';
import type { Expense, ExpenseCategory, ExpenseUnsettable } from '../../types/expense';
import type { Vehicle } from '../../types/vehicle';
import type { Driver } from '../../types/driver';
import type { ExpenseCreateDto, ExpensePatchDto } from '../../lib/api/endpoints';
import { mensajeErrorGasto, parseMontoCOP, formatearCOP } from '../../utils/expenses';
import { fromLocalInputValue, toLocalInputValue } from '../../utils/trips';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Presente = CORREGIR ese gasto. Ausente = registrar uno nuevo. */
  expense?: Expense | null;
  /**
   * El catálogo **completo**, no solo las activas: al corregir hay que poder
   * enseñar la categoría del propio gasto aunque se haya desactivado después.
   * El filtrado lo hace el componente (`ofrecibles`).
   */
  categorias: ExpenseCategory[];
  vehicles: Vehicle[];
  drivers: Driver[];
  /** Preselección al registrar desde una superficie que ya sabe de qué cuelga. */
  vehicleIdInicial?: string;
  driverIdInicial?: string;
  tripIdInicial?: string;
  /** El Operador no elige de quién es el gasto: el servidor le estampa su conductor. */
  ocultarAsociaciones?: boolean;
  /**
   * `true` cuando quien registra es un **Operador**: el servidor le estampa su
   * propio conductor pase lo que pase en el cuerpo, así que la asociación
   * obligatoria ya está garantizada y exigirla en el cliente bloquearía un
   * registro que el backend aceptaría sin problema.
   */
  driverAutomatico?: boolean;
  onRegistrar: (data: ExpenseCreateDto, recibo: File | null) => Promise<unknown>;
  onCorregir: (expenseId: string, data: ExpensePatchDto) => Promise<unknown>;
}

interface FormState {
  categoryId: string;
  amount: string;
  incurredAt: string;
  vehicleId: string;
  driverId: string;
  notes: string;
  reason: string;
}

function ahoraLocal(): string {
  return toLocalInputValue(new Date().toISOString());
}

function desdeExpense(e: Expense): FormState {
  return {
    categoryId: e.categoryId,
    // Canónico, sin formatear: el campo se edita, y meter separadores de miles
    // en un input que la persona va a reescribir solo estorba. `parseMontoCOP`
    // acepta las dos formas al enviar.
    amount: e.amount,
    incurredAt: toLocalInputValue(e.incurredAt),
    vehicleId: e.vehicleId ?? '',
    driverId: e.driverId ?? '',
    // Con `trim`: si la nota guardada trae un espacio de más, comparar en crudo
    // contra lo que el usuario deja en el campo mete una línea de "cambio" en
    // una novedad que no se puede borrar, por una diferencia que nadie ve.
    notes: (e.notes ?? '').trim(),
    reason: '',
  };
}

/**
 * FE-12 — registrar un gasto y corregirlo son el MISMO formulario.
 *
 * Cuatro cosas de aquí no son evidentes y las cuatro vienen del contrato:
 *
 *  1. **El importe se escribe como una persona lo escribe.** `50.000` con punto
 *     de miles NO pasa el patrón `Money` del contrato y devolvería 400.
 *     `parseMontoCOP` lo normaliza a `"50000.00"`, y debajo del campo se enseña
 *     cómo quedó — que es la única forma de que alguien detecte que su `50,5`
 *     se leyó como cincuenta con cincuenta y no como cincuenta mil.
 *
 *  2. **`incurredAt` por defecto es AHORA, pero se puede mover al pasado.** Es
 *     el campo que decide a qué mes y a qué viaje pertenece el gasto (ADR-024).
 *     Un peaje de anteayer digitado hoy tiene que poder declararse con su fecha
 *     real, o el expediente del viaje de anteayer nunca lo verá.
 *
 *  3. **Quitar una asociación NO es mandarla vacía, es `unset`.** En JSON un
 *     campo ausente y uno en `null` llegan indistinguibles al backend, así que
 *     el contrato pide la intención explícita.
 *
 *  4. **El recibo NO viaja en este formulario.** Se sube en una segunda llamada
 *     después de que el gasto exista, porque el endpoint cuelga del id del
 *     gasto. Por eso `onRegistrar` recibe el archivo aparte, y por eso el
 *     recibo es opcional: si la subida falla, el gasto ya está registrado y se
 *     completa desde su detalle. En modo **corrección** el bloque no aparece —
 *     el recibo se cambia desde el detalle, donde se ven sus versiones.
 *
 * En modo corrección solo se envía lo que cambió: mandar el formulario entero
 * haría que cada corrección apareciera en la novedad tocando los seis campos,
 * cuando quizá solo se ajustó el importe.
 */
export function ExpenseFormModal({
  isOpen, onClose, expense, categorias, vehicles, drivers,
  vehicleIdInicial, driverIdInicial, tripIdInicial, ocultarAsociaciones = false,
  driverAutomatico = false, onRegistrar, onCorregir,
}: Props) {
  const esCorreccion = !!expense;
  const [form, setForm] = useState<FormState>({
    categoryId: '', amount: '', incurredAt: '', vehicleId: '', driverId: '', notes: '', reason: '',
  });
  const [recibo, setRecibo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setRecibo(null);
    if (expense) {
      setForm(desdeExpense(expense));
      return;
    }
    setForm({
      // Sin preselección de categoría: elegir "Alimentación" por ser la primera
      // de la lista sería poner una etiqueta que nadie decidió, y el catálogo
      // puede tener categorías propias del tenant en cualquier orden.
      categoryId: '',
      amount: '',
      incurredAt: ahoraLocal(),
      vehicleId: vehicleIdInicial ?? '',
      driverId: driverIdInicial ?? '',
      notes: '',
      reason: '',
    });
  }, [isOpen, expense, vehicleIdInicial, driverIdInicial]);

  const set = (campo: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [campo]: e.target.value }));

  const montoCanonico = parseMontoCOP(form.amount);

  /**
   * Las categorías que el desplegable ofrece: las activas, **más la del propio
   * gasto** si se desactivó después de registrarlo.
   *
   * Sin esa segunda parte, corregir un gasto de una categoría retirada dejaba
   * el `<select>` sin ninguna opción que casara con su `value`: el navegador lo
   * pinta **vacío**, el usuario lee que el gasto no tiene categoría —y sí la
   * tiene— y al tocar el desplegable la reasigna sin querer, con la corrección
   * quedando en la novedad a su nombre. Se marca `(inactiva)`, igual que hace
   * el filtro del listado.
   */
  const ofrecibles = categorias.filter(c => c.active || c.id === form.categoryId);

  const validar = (): string | null => {
    if (!form.categoryId) return 'Elige la categoría del gasto.';
    if (!form.amount.trim()) return 'Indica el importe.';
    if (!montoCanonico) return 'El importe no es válido. Escribe una cifra mayor que cero, por ejemplo 50.000 o 50000.';
    if (!form.incurredAt) return 'Indica cuándo se hizo el gasto.';
    // La asociación la exige el backend con un 400. Se comprueba también aquí
    // para explicar POR QUÉ, que es algo que un 400 genérico no cuenta.
    //
    // ⚠️ **El VIAJE cuenta como asociación** (`console/expenses.go`,
    // `validarAsociacion`), y al corregir hay que mirar el del gasto que se
    // está editando: `desdeExpense` no lo trae al formulario porque no se
    // edita, pero existe. Sin esto, un gasto cuya única asociación es su viaje
    // —que el contrato permite— quedaba **imposible de corregir**: la
    // validación saltaba siempre y el `PATCH` no llegaba a salir.
    const asociado = tieneAsociacion({
      vehicleId: form.vehicleId || undefined,
      driverId: form.driverId || undefined,
      tripId: tripIdInicial || expense?.tripId,
    });
    if (!ocultarAsociaciones && !driverAutomatico && !asociado) {
      return 'El gasto tiene que colgar de un vehículo, un conductor o un viaje: uno suelto no se puede reportar ni auditar.';
    }
    if (esCorreccion && !form.reason.trim()) {
      return 'La razón de la corrección es obligatoria — queda en la novedad.';
    }
    return null;
  };

  const enviar = async () => {
    const problema = validar();
    if (problema) { setError(problema); return; }

    setEnviando(true);
    setError(null);
    try {
      if (esCorreccion && expense) {
        const original = desdeExpense(expense);
        const cambios: ExpensePatchDto = { reason: form.reason.trim() };
        const unset: ExpenseUnsettable[] = [];

        if (form.categoryId !== original.categoryId) cambios.categoryId = form.categoryId;
        // Se compara el CANÓNICO contra el canónico: `50.000` y `50000.00` son
        // el mismo importe, y anotar en la novedad un cambio de formato sería
        // ruido en un registro contable.
        if (montoCanonico && montoCanonico !== original.amount) cambios.amount = montoCanonico;
        if (form.incurredAt !== original.incurredAt) cambios.incurredAt = fromLocalInputValue(form.incurredAt);
        if (form.vehicleId !== original.vehicleId) {
          if (form.vehicleId) cambios.vehicleId = form.vehicleId;
          else unset.push('vehicleId');
        }
        if (form.driverId !== original.driverId) {
          if (form.driverId) cambios.driverId = form.driverId;
          else unset.push('driverId');
        }
        if (form.notes.trim() !== original.notes) {
          if (form.notes.trim()) cambios.notes = form.notes.trim();
          else unset.push('notes');
        }
        if (unset.length > 0) cambios.unset = unset;

        // El backend responde 200 sin anotar cuando nada cambió, pero decirlo
        // aquí evita una petición que el usuario leería como "se guardó".
        if (Object.keys(cambios).length === 1) {
          setError('No cambiaste ningún dato del gasto.');
          setEnviando(false);
          return;
        }
        await onCorregir(expense.id, cambios);
      } else {
        await onRegistrar({
          categoryId: form.categoryId,
          amount: montoCanonico!,
          incurredAt: fromLocalInputValue(form.incurredAt)!,
          vehicleId: form.vehicleId || undefined,
          driverId: form.driverId || undefined,
          tripId: tripIdInicial || undefined,
          notes: form.notes.trim() || undefined,
        }, recibo);
      }
      onClose();
    } catch (err) {
      setError(mensajeErrorGasto(err, esCorreccion ? 'corregir' : 'registrar'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={esCorreccion ? 'Corregir gasto' : 'Registrar gasto'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button onClick={enviar} isLoading={enviando}>
            {esCorreccion ? 'Guardar corrección' : 'Registrar'}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        {esCorreccion && (
          <div style={{ display: 'flex', gap: '0.625rem', padding: '0.75rem 0.875rem', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-lg)', color: '#F59E0B', fontSize: '12px' }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>
              Cambiar la <strong>fecha del gasto</strong> puede sacarlo del expediente de un viaje y
              meterlo en el de otro: el expediente se deriva de la ventana, no está estampado. La
              corrección queda en la novedad con el de qué a qué.
            </span>
          </div>
        )}

        {ofrecibles.length === 0 && (
          <div style={{ display: 'flex', gap: '0.625rem', padding: '0.75rem 0.875rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-lg)', color: '#EF4444', fontSize: '12px' }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>El catálogo de categorías está vacío o todas están desactivadas. Un Administrador tiene que habilitar al menos una.</span>
          </div>
        )}

        <Select
          label="Categoría"
          value={form.categoryId}
          onChange={set('categoryId')}
          options={[
            { value: '', label: 'Selecciona una categoría…' },
            ...ofrecibles.map(c => ({ value: c.id, label: c.active ? c.label : `${c.label} (inactiva)` })),
          ]}
        />

        <Input
          label="Importe (COP)"
          value={form.amount}
          onChange={set('amount')}
          inputMode="decimal"
          placeholder="50.000"
        />
        {/* Rojo SOLO cuando hay algo escrito que no se entiende. Un campo
            vacío todavía no es un error: pintarlo en rojo nada más abrir el
            formulario enseña a ignorar el color. */}
        <p className={`form-hint${form.amount.trim() && !montoCanonico ? ' form-hint--error' : ''}`}>
          {form.amount.trim() === ''
            ? 'Escríbelo como quieras: 50.000, 50000 o 50000,50.'
            : montoCanonico
              ? `Se registrará ${formatearCOP(montoCanonico)}.`
              : 'No se entiende esa cifra. Prueba con 50.000 o 50000.'}
        </p>

        <Input
          label="Cuándo se hizo el gasto"
          type="datetime-local"
          value={form.incurredAt}
          onChange={set('incurredAt')}
        />
        <p className="form-hint">
          Cuándo se <strong>incurrió</strong>, no cuándo lo estás digitando. Decide a qué mes y a qué viaje pertenece.
        </p>

        {!ocultarAsociaciones && (
          <>
            {/* Los dos juntos a propósito: comparten una sola ayuda, así que
                tienen que leerse como pareja. Se apilan por debajo de 560 px. */}
            <div className="form-pair">
              <Select
                label="Vehículo"
                value={form.vehicleId}
                onChange={set('vehicleId')}
                options={[
                  { value: '', label: 'Sin vehículo' },
                  ...vehicles.map(v => ({ value: v.id, label: `${v.plate}${v.model ? ` · ${v.model}` : ''}` })),
                ]}
              />

              <Select
                label="Conductor"
                value={form.driverId}
                onChange={set('driverId')}
                options={[
                  { value: '', label: 'Sin conductor' },
                  ...drivers.map(d => ({ value: d.id, label: d.name })),
                ]}
              />
            </div>

            <p className="form-hint">
              Al menos uno de los dos. Un peaje puede no tener conductor identificado, y un viático
              no tiene vehículo.
            </p>
          </>
        )}

        {ocultarAsociaciones && (
          <p className="form-hint">
            El gasto queda a tu nombre y a tu vehículo asignado — no hace falta elegirlos.
          </p>
        )}

        {driverAutomatico && !ocultarAsociaciones && !esCorreccion && (
          <p className="form-hint">
            El gasto queda a tu nombre aunque no elijas nada. El vehículo sí conviene ponerlo: un
            tanqueo sin vehículo no dice de qué equipo fue.
          </p>
        )}

        {tripIdInicial && !esCorreccion && (
          <p className="form-hint">
            Se asignará a este viaje de forma explícita, así que seguirá en su expediente aunque
            después se corrija la ventana del viaje.
          </p>
        )}

        <Textarea
          label="Nota (opcional)"
          value={form.notes}
          onChange={set('notes')}
          rows={2}
          placeholder="Peaje Lebrija, sentido norte"
        />

        {!esCorreccion && (
          <>
            <ReceiptPicker file={recibo} onChange={setRecibo} disabled={enviando} />
            <p className="form-hint">
              Puedes registrar el gasto sin foto y adjuntarla después desde su detalle. Las fotos se
              comprimen antes de subir para que salgan rápido con señal mala.
            </p>
          </>
        )}

        {esCorreccion && (
          <Input
            label="Razón de la corrección"
            value={form.reason}
            onChange={set('reason')}
            placeholder="El recibo decía 62.000, no 50.000"
          />
        )}

        {error && (
          <p style={{ margin: 0, fontSize: '12.5px', color: '#EF4444', fontWeight: 600 }}>{error}</p>
        )}
      </div>
    </Modal>
  );
}
